import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { apiError, TimePunchType, type TimeEntry, type TimePunch } from './lib';

/**
 * Amend a punch, or record one that was never taken.
 *
 * ADR-0008 §5 — attendance records are evidence. Neither path here is an
 * edit: `correctPunch` writes a NEW `TimePunch` carrying `supersedesId`, and
 * a Postgres trigger refuses any `UPDATE` that would change the original's
 * `occurredAt` — so the copy on this dialog says "amend" and never "edit",
 * and the reason field is not decoration, it is the one thing that makes the
 * new row different from a rewrite.
 */
export default function CorrectionDialog({
  entry,
  punch,
  onClose,
  onSaved,
}: {
  entry: TimeEntry;
  /** The punch being amended, or `null` to record one that was never taken. */
  punch: TimePunch | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isAmend = punch !== null;

  const [occurredAt, setOccurredAt] = useState(
    (punch?.occurredAt ?? new Date().toISOString()).slice(0, 16),
  );
  const [type, setType] = useState<TimePunchType>(TimePunchType.CLOCK_IN);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!reason.trim() || !occurredAt) return;
    setSaving(true);
    try {
      const occurredIso = new Date(occurredAt).toISOString();
      if (isAmend && punch) {
        await api.post('/attendance/punches/correct', {
          punchId: punch.id,
          occurredAt: occurredIso,
          reason: reason.trim(),
        });
      } else {
        await api.post('/attendance/punches/missing', {
          employeeId: entry.employeeId,
          branchId: entry.branchId,
          type,
          occurredAt: occurredIso,
          reason: reason.trim(),
          timeEntryId: entry.id,
        });
      }
      toast.success(t('workforce.correction.saved'));
      onSaved();
    } catch (e) {
      toast.error(apiError(e, t('workforce.correction.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(isAmend ? 'workforce.correction.amendTitle' : 'workforce.correction.addMissingTitle')}
      description={t(isAmend ? 'workforce.correction.amendHint' : 'workforce.correction.addMissingHint')}
      className="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving || !reason.trim()}>
            {t('workforce.correction.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!isAmend && (
          <Field label={t('workforce.correction.punchType')}>
            <Select value={type} onChange={(e) => setType(e.target.value as TimePunchType)}>
              {Object.values(TimePunchType).map((pt) => (
                <option key={pt} value={pt}>
                  {t(`workforce.punchType.${pt}`)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field
          label={t(isAmend ? 'workforce.correction.correctedTime' : 'workforce.correction.actualTime')}
        >
          <Input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
        </Field>

        <Field
          label={t('workforce.correction.reason')}
          required
          hint={t('workforce.correction.reasonHint')}
        >
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
